import { Component, type ReactNode } from 'react'

// Top-level fallback shown when the React/Inertia tree throws. It sits ABOVE the
// Inertia provider, so it can't use usePage()/useTranslations() (no context) and
// is a class component (no hooks) — and it must stay dependency-free so the
// fallback itself can never crash. We still localize by reading the <html lang>
// attribute, which the server sets per request.
const COPY: Record<string, { title: string; body: string; reload: string }> = {
  en: {
    title: 'Something went wrong',
    body: 'An unexpected error occurred. Try reloading the page.',
    reload: 'Reload page',
  },
  de: {
    title: 'Etwas ist schiefgelaufen',
    body: 'Ein unerwarteter Fehler ist aufgetreten. Bitte laden Sie die Seite neu.',
    reload: 'Seite neu laden',
  },
}

function fallbackCopy() {
  const lang = typeof document !== 'undefined' ? document.documentElement.lang : 'en'
  return COPY[lang] ?? COPY.en
}

interface ErrorBoundaryProps {
  children: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export default class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack)
  }

  render() {
    if (this.state.hasError) {
      const copy = fallbackCopy()
      return (
        <div className="flex min-h-screen items-center justify-center bg-brand-offwhite px-6">
          <div className="max-w-md text-center">
            <h1 className="font-heading text-2xl font-bold text-brand-blue">{copy.title}</h1>
            <p className="mt-2 text-sm text-brand-grey">{copy.body}</p>
            {this.state.error && import.meta.env.DEV && (
              <pre className="mt-4 max-h-32 overflow-auto rounded-lg bg-zinc-50 p-3 text-left text-xs text-brand-grey">
                {this.state.error.message}
              </pre>
            )}
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-6 rounded-lg bg-brand-blue px-4 py-2 text-sm font-semibold text-white hover:opacity-90"
            >
              {copy.reload}
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
